/*
 * cart-addons.js — native Horizon cart-drawer add-ons for Rovvik.
 *
 * Shipping protection (always-on by default):
 *   - Auto-adds the protection product whenever the cart has real items and the
 *     customer hasn't switched it off this session.
 *   - The toggle adds / removes the protection line on demand.
 *   - Cleans up the protection line if it's the only thing left in the cart.
 *
 * Refresh model: after any cart mutation we dispatch a plain `cart:update`
 * CustomEvent. Horizon's <cart-items-component> listens for it and re-renders
 * the drawer (falling back to sectionRenderer when no sections payload is
 * present), which re-renders this snippet with fresh data-* state.
 */
(function () {
  'use strict';

  var ADD_URL = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
  var CHANGE_URL = (window.Theme && window.Theme.routes && window.Theme.routes.cart_change_url) || '/cart/change.js';
  var CART_URL = '/cart.js';
  var SESSION_OFF_KEY = 'rvk_protection_off';

  var working = false;

  function getCart() {
    return fetch(CART_URL, { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  function isProtection(item) {
    return !!(item && item.properties && item.properties._protection);
  }

  function fireCartUpdate() {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: { data: { source: 'cart-addons' } },
      })
    );
  }

  function addProtection(variantId) {
    if (working || !variantId) return Promise.resolve();
    working = true;
    return fetch(ADD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1, properties: { _protection: 'protection' } }],
      }),
    })
      .then(function () {
        working = false;
        fireCartUpdate();
      })
      .catch(function () {
        working = false;
      });
  }

  function removeLine(key) {
    if (working || !key) return Promise.resolve();
    working = true;
    return fetch(CHANGE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 }),
    })
      .then(function () {
        working = false;
        fireCartUpdate();
      })
      .catch(function () {
        working = false;
      });
  }

  /* ---- Upsell carousel ---------------------------------------------------- */

  function addVariant(variantId) {
    if (!variantId) return Promise.resolve();
    return fetch(ADD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
    }).then(function () {
      fireCartUpdate();
    });
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function money(cents) {
    return '$' + (Number(cents || 0) / 100).toFixed(2);
  }

  function pickVariant(product) {
    var v = (product.variants || []).filter(function (x) {
      return x.available;
    })[0];
    return v || (product.variants || [])[0];
  }

  function cardHtml(product) {
    var v = pickVariant(product);
    if (!v) return '';
    var img = product.featured_image || (product.images && product.images[0]) || '';
    var imgTag = img
      ? '<img class="cart-upsell__img" src="' + escapeHtml(img) + '" alt="" loading="lazy" width="44" height="44">'
      : '';
    return (
      '<div class="cart-upsell__card">' +
      '<div class="cart-upsell__card-main">' +
      imgTag +
      '<div class="cart-upsell__info">' +
      '<p class="cart-upsell__title">' + escapeHtml(product.title) + '</p>' +
      '<p class="cart-upsell__price">' + money(v.price) + '</p>' +
      '</div></div>' +
      '<button type="button" class="cart-upsell__add" data-upsell-add data-variant-id="' + v.id + '">ADD</button>' +
      '</div>'
    );
  }

  function renderUpsell() {
    var root = document.querySelector('[data-upsell-root]');
    if (!root) return;
    var track = root.querySelector('[data-upsell-track]');
    var seed = root.getAttribute('data-seed');
    var limit = Number(root.getAttribute('data-limit')) || 6;
    if (!seed || !track) {
      root.hidden = true;
      return;
    }

    Promise.all([
      fetch('/recommendations/products.json?product_id=' + encodeURIComponent(seed) + '&limit=' + (limit + 6) + '&intent=related', {
        headers: { Accept: 'application/json' },
      })
        .then(function (r) {
          return r.json();
        })
        .catch(function () {
          return { products: [] };
        }),
      getCart(),
    ]).then(function (res) {
      var recs = (res[0] && res[0].products) || [];
      var cart = res[1] || { items: [] };
      var inCart = {};
      cart.items.forEach(function (i) {
        inCart[i.product_id] = true;
      });
      var cards = recs
        .filter(function (p) {
          return !inCart[p.id] && p.available;
        })
        .slice(0, limit);

      if (!cards.length) {
        track.innerHTML = '';
        root.hidden = true;
        return;
      }
      track.innerHTML = cards.map(cardHtml).join('');
      root.hidden = false;
      updateArrows(root, track);
    });
  }

  function updateArrows(root, track) {
    var prev = root.querySelector('[data-upsell-prev]');
    var next = root.querySelector('[data-upsell-next]');
    if (!prev || !next) return;
    var maxScroll = track.scrollWidth - track.clientWidth - 1;
    var overflow = maxScroll > 0;
    prev.disabled = !overflow || track.scrollLeft <= 0;
    next.disabled = !overflow || track.scrollLeft >= maxScroll;
    prev.style.display = overflow ? '' : 'none';
    next.style.display = overflow ? '' : 'none';
  }

  // Upsell clicks: ADD buttons + arrows.
  document.addEventListener('click', function (e) {
    var addBtn = e.target.closest && e.target.closest('[data-upsell-add]');
    if (addBtn) {
      addBtn.disabled = true;
      addBtn.textContent = '✓';
      addVariant(addBtn.getAttribute('data-variant-id'));
      return;
    }
    var arrow = e.target.closest && e.target.closest('[data-upsell-prev], [data-upsell-next]');
    if (arrow) {
      var root = arrow.closest('[data-upsell-root]');
      var track = root && root.querySelector('[data-upsell-track]');
      if (!track) return;
      var dir = arrow.hasAttribute('data-upsell-next') ? 1 : -1;
      track.scrollBy({ left: dir * 170, behavior: 'smooth' });
      setTimeout(function () {
        updateArrows(root, track);
      }, 350);
    }
  });

  document.addEventListener(
    'scroll',
    function (e) {
      if (e.target && e.target.matches && e.target.matches('[data-upsell-track]')) {
        var root = e.target.closest('[data-upsell-root]');
        if (root) updateArrows(root, e.target);
      }
    },
    true
  );

  /* Decide whether to auto-add or clean up, based on the LIVE cart (not the
     possibly-stale rendered toggle) so we never double-add. */
  function syncProtection() {
    var toggle = document.getElementById('cart-protection-toggle');
    if (!toggle) return;

    var variantId = toggle.getAttribute('data-variant-id');
    var defaultOn = toggle.getAttribute('data-default-on') === 'true';
    var toggledOff = sessionStorage.getItem(SESSION_OFF_KEY) === 'true';

    getCart().then(function (cart) {
      var protection = cart.items.filter(isProtection)[0];
      var realItems = cart.items.filter(function (i) {
        return !isProtection(i);
      });

      // Keep the toggle's visual state in sync with the real cart. Liquid always
      // renders it checked when default-on, so correct it for the opted-out case.
      if (protection) {
        toggle.checked = true;
      } else if (toggledOff || realItems.length === 0 || !defaultOn) {
        toggle.checked = false;
      } else {
        toggle.checked = true; // about to auto-add below
      }

      // Clean up a stranded protection line.
      if (protection && realItems.length === 0) {
        removeLine(protection.key);
        return;
      }

      // Auto-add when on-by-default, real items present, not already in, not opted out.
      if (defaultOn && !toggledOff && !protection && realItems.length > 0) {
        addProtection(variantId);
      }
    });
  }

  // Delegated toggle handler (the checkbox is replaced on every drawer re-render).
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || t.id !== 'cart-protection-toggle') return;
    if (t.checked) {
      sessionStorage.removeItem(SESSION_OFF_KEY);
      addProtection(t.getAttribute('data-variant-id'));
    } else {
      sessionStorage.setItem(SESSION_OFF_KEY, 'true');
      getCart().then(function (cart) {
        var protection = cart.items.filter(isProtection)[0];
        if (protection) removeLine(protection.key);
      });
    }
  });

  // Re-evaluate after every cart change (debounced to let the drawer settle).
  var t;
  document.addEventListener('cart:update', function () {
    clearTimeout(t);
    t = setTimeout(function () {
      syncProtection();
      renderUpsell();
    }, 250);
  });

  function init() {
    setTimeout(function () {
      syncProtection();
      renderUpsell();
    }, 300);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
